import React, { useState, useEffect } from 'react';
import { notesAPI } from '../utils/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { StickyNote, Plus, Trash2, User, Clock } from 'lucide-react';

export default function CaseNotes({ caseId, currentUserId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (caseId) {
      fetchNotes();
    }
  }, [caseId]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await notesAPI.getNotes(caseId);
      setNotes(response.data || []);
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      setSubmitting(true);
      await notesAPI.addNote(caseId, newNote.trim());
      setNewNote('');
      fetchNotes();
    } catch (err) {
      console.error('Error adding note:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      setDeletingId(noteId);
      await notesAPI.deleteNote(caseId, noteId);
      fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(dateString);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <StickyNote className="h-5 w-5" />
          Internal Notes
          <Badge variant="outline" className="ml-2 text-xs">
            Staff Only
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Note Form */}
        <form onSubmit={handleAddNote} className="space-y-2">
          <textarea
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm resize-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            rows={3}
            placeholder="Add an internal note... (e.g., client communication summary, risk assessment, additional observations)"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <div className="flex justify-end">
            <Button 
              type="submit" 
              size="sm" 
              disabled={!newNote.trim() || submitting}
            >
              <Plus className="h-4 w-4 mr-1" />
              {submitting ? 'Adding...' : 'Add Note'}
            </Button>
          </div>
        </form>

        {/* Notes List */}
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin h-6 w-6 border-2 border-teal-600 border-t-transparent rounded-full"></div>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <StickyNote className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p>No internal notes yet</p>
            <p className="text-xs">Add notes to track important case information</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {notes.map((note) => (
              <div 
                key={note.id} 
                className="bg-gray-50 rounded-lg p-3 border border-gray-100 hover:border-gray-200 transition-colors"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <User className="h-3 w-3" />
                    <span className="font-medium">{note.author_name}</span>
                    <span>•</span>
                    <Clock className="h-3 w-3" />
                    <span title={formatDate(note.created_at)}>{getRelativeTime(note.created_at)}</span>
                  </div>
                  {(note.author_id === currentUserId || true) && ( // Allow deletion for authorized users
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                      onClick={() => handleDeleteNote(note.id)}
                      disabled={deletingId === note.id}
                    >
                      {deletingId === note.id ? (
                        <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.note}</p>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
          <p className="text-xs text-yellow-800">
            <strong>Note:</strong> Internal notes are only visible to Admin and Case Officers. 
            Clients cannot see these notes.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
